import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  LinearProgressProps,
  CircularProgress,
  Typography
} from '@mui/material';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import './AnalyzerModal.css';
import DialogConfirm from '../DialogConfirm';
import { SessionTable } from './SessionTable';
import { EpSessions } from '../Dashboards/ApplicationHighlightsDashboard';

interface S3AnalyzerModalProps {
  open: boolean;
  handleClose: () => void;
}

export const S3AnalyzerModal = ({ open, handleClose }: S3AnalyzerModalProps) => {
  const [showConfirmCloseDialog, setShowConfirmCloseDialog] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [cancelToken, setCancelToken] = useState<any>();
  const [showTable, setShowTable] = useState<boolean>(false);
  const [percentage, setPercentage] = useState<number>(0);
  const [sessions, setSessions] = useState<EpSessions[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [mounted, setMounted] = useState(false);

  const api = useApi();

  const MAX_FILE_SIZE = 10737418240;

  useEffect(() => {
    setMounted(true);
  }, []);

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    disabled: isUploading,
    validator: (file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          'Sorry, we can only accept file size less than 10GB at this time. You may filter this pcap file with a wireshark to reduce its size and try again.',
          { autoClose: false }
        );
        handleClose();
        return {
          code: 'file-too-large',
          message: 'File size exceeds 10GB limit.'
        }
      }
      return null;
    }
  });

  const handleModalClose = () => {
    setShowConfirmCloseDialog(true);
  };

  const files = acceptedFiles.map(file => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));

  const { data: uploadData, refetch: refetchUploadData, } = useQuery(
    ['uploadData'],
    api.getS3UploadUrlApiV1GetS3UploadUrlGet.bind(null, {
      pcap_file_name: acceptedFiles[0]?.name || ''
    }),
    { enabled: false },
  )

  const startAnalyze = async () => {
    try {
      const { data } = await refetchUploadData();
      const s3Url = data?.data.s3_url;
      const pcapFileKey = data?.data.pcap_file_key;
      const pcapFileName = data?.data.pcap_file_name;
      if (!s3Url || !pcapFileKey || !pcapFileName) {
        toast.error('Failed to get upload details.');
        return;
      }
      const source = axios.CancelToken.source();
      setCancelToken(source);
      setIsUploading(true);
      await axios.put(s3Url, acceptedFiles[0], {
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          let percentage = total ? Math.floor((loaded * 100) / total) : 0;
          setUploadProgress(percentage);
        },
        headers: { 'Content-Type': '' },
        cancelToken: source.token,
      });
      setIsProcessing(true);
      analyzeS3PCAP.mutate({
        pcap_file_key: pcapFileKey,
        pcap_file_name: pcapFileName,
      });
    } catch (error) {
      setIsProcessing(false);
      setIsUploading(false);
      handleError(error);
    }
  };

  const analyzeS3PCAP = useMutation(api.analyzeS3PcapApiV1AnalyzeS3PcapGet, {
    onSuccess(data) {
      const newSessions = (data?.data as any).sessions;
      if (newSessions?.length) {
        setPercentage(newSessions.filter((s: EpSessions) => s.performance !== 'good').length * 100 / newSessions.length)
      } else setPercentage(0);
      setSessions(newSessions?.map((item: EpSessions, i: number) => ({
        ...item,
        id: `${item.node_id}_${item.start_time}_${i}`,
      })) || []);
      setIsProcessing(false);
      setShowTable(true);
    },
    onError: apiError
  })

  const handleError = (err: any ) => {
    if (err.response) {
      const status = err.response.status;
      if (status === 400) {
        toast.error(err.response.data.detail);
      } else if (status === 413) {
        toast.error('Error: Chosen file is too large to upload.');
      } else if (status === 500) {
        toast.error(err.response.data.detail);
      } else {
        toast.error(`Error ${status}: ${err.message}`);
      }
    } else if (err.code && err.message) {
      err.code !== 'ERR_CANCELED' && toast.error(`Error ${err.code}: ${err.message}`);
    } else {
      toast.error(`Unknown error`);
    }
  }

  useEffect(() => {
    mounted && acceptedFiles[0] && startAnalyze();
  }, [mounted, acceptedFiles]);

  const closeModal = () => {
    if (cancelToken) cancelToken.cancel();
    handleClose();
  };

  function UploadProgressWithLabel(props: LinearProgressProps & { value: number }) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 5 }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress variant="determinate" {...props} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary' }}
          >{`${Math.round(props.value)}%`}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xl"
        onClose={handleModalClose}
        aria-labelledby="standard-dialog"
        aria-describedby="standard-dialog-description"
      >
        <DialogTitle
          id="standard-dialog"
          sx={{ fontSize: '18px', display: 'flex', alignItems: 'center' }}
          color="primary"
        >
          Free PCAP Analyzer
        </DialogTitle>
        <DialogContent>
          {isProcessing && (
            <Box sx={{ textAlign: 'center', my: 3 }}>
              <Typography variant="h6" color="primary">
                Processing, please wait...
              </Typography>
              <CircularProgress sx={{ mt: 2 }} />
            </Box>
          )}
          {showTable ?
            <SessionTable
              percentage={percentage}
              sessions={sessions}
              title={`Pcap file`}
              fromModal={true}
            /> :
            <>
              <section className="container">
                <div {...getRootProps({className: 'dropzone'})}>
                  <input {...getInputProps()} />
                  <p>Drag 'n' drop a pcap file here, or click to select file.</p>
                </div>
                {acceptedFiles && acceptedFiles.length ? <aside>
                  <h4>File Info</h4>
                  <ul>{files}</ul>
                </aside> : null}
              </section>
              {acceptedFiles && acceptedFiles.length && isUploading
                ? <Box sx={{ width: '100%' }}>
                    <UploadProgressWithLabel
                      color="info"
                      sx={{ height: 10 }}
                      value={uploadProgress}
                    />
                  </Box>
                : null
              }
            </>
          }
        </DialogContent>
        <DialogActions>
          <IconButton
            sx={{
              position: 'absolute',
              top: '8px',
              right: '8px',
            }}
            onClick={handleModalClose}
          >
            <HighlightOffOutlinedIcon fontSize="small"/>
          </IconButton>
        </DialogActions>
      </Dialog>
      {showConfirmCloseDialog && (
        <DialogConfirm
          open={showConfirmCloseDialog}
          handleClose={() => setShowConfirmCloseDialog(false)}
          handleConfirm={closeModal}
          title="Close PCAP analyzer?"
          description="Are you certain you wish to proceed with closing PCAP analyzer?"
        />
      )}
    </>
  );
};
